import { useEffect, useState } from 'react';
import { STREAM_CATEGORIES, type PublicStream, type StreamCategory } from '@zip2live/shared';
import { Avatar, Header, Link, LiveBadge } from '../components/Brand.tsx';
import { CATEGORY_EMOJI, CategoryTag, count, WatchingPill } from '../components/StreamStats.tsx';
import { ThumbUpIcon } from '../components/Icons.tsx';
import { api, type LiveNow as LiveNowData } from '../lib/api.ts';
import { navigate } from '../lib/router.ts';

type Sort = 'trending' | 'viewers' | 'new';
const SORTS: [Sort, string][] = [['trending', 'Trending'], ['viewers', 'Most watched'], ['new', 'Just started']];
const LABEL = new Map<string, string>(STREAM_CATEGORIES.map((c) => [c.id, c.label]));

/** zip2live.com/live: public streams live right now, by category, trending first. */
export function LiveNow() {
  const params = new URLSearchParams(location.search);
  const [category, setCategory] = useState<StreamCategory | null>((params.get('category') as StreamCategory | null) ?? null);
  const [sort, setSort] = useState<Sort>((params.get('sort') as Sort | null) ?? 'trending');
  const [data, setData] = useState<LiveNowData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.liveNow(category, sort).then(
        (d) => alive && (setData(d), setError(false)),
        () => alive && setError(true),
      );
    void load();
    // Numbers move every few seconds; the list is refreshed twice a minute.
    const t = setInterval(load, 30_000);
    const qs = new URLSearchParams({ ...(category ? { category } : {}), ...(sort !== 'trending' ? { sort } : {}) }).toString();
    history.replaceState(null, '', `/live${qs ? `?${qs}` : ''}`);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [category, sort]);

  const chip = (active: boolean) => `inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-extrabold ${active ? 'bg-white text-navy' : 'bg-white/10 text-white hover:bg-white/20'}`;

  return (
    <main className="min-h-full bg-navy-900 pb-20 text-white">
      <Header tone="dark" />
      <div className="mx-auto max-w-6xl px-4 pt-4 sm:px-8 sm:pt-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="speed-text text-[clamp(2.5rem,7vw,3.5rem)] leading-none">
              Live <span className="text-race-300">now</span>
            </h1>
            <p className="mt-2 text-muted-dark">Public streams happening right now. Tap one to watch.</p>
          </div>
          <div className="flex gap-1 rounded-full bg-white/10 p-1" role="tablist" aria-label="Order">
            {SORTS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={sort === id} onClick={() => setSort(id)} className={`h-9 rounded-full px-3.5 text-sm font-bold ${sort === id ? 'bg-white text-navy' : 'text-white/80 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="-mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Category">
          <button type="button" role="tab" aria-selected={!category} className={chip(!category)} onClick={() => setCategory(null)}>
            All {data ? <span className="opacity-60">{data.total}</span> : null}
          </button>
          {data?.categories.map((c) => (
            <button key={c.id} type="button" role="tab" aria-selected={category === c.id} className={chip(category === c.id)} onClick={() => setCategory(c.id)}>
              {CATEGORY_EMOJI[c.id]} {LABEL.get(c.id)} <span className="opacity-60">{c.count}</span>
            </button>
          ))}
          {category && !data?.categories.some((c) => c.id === category) && (
            <button type="button" role="tab" aria-selected className={chip(true)} onClick={() => setCategory(null)}>
              {CATEGORY_EMOJI[category]} {LABEL.get(category)} <span className="opacity-60">0</span>
            </button>
          )}
        </div>

        {!data && !error && <p className="mt-10 font-semibold text-white/70">Loading…</p>}
        {error && !data && <p className="mt-10 font-semibold text-white/70">Couldn’t load Live Now. It retries in a moment.</p>}
        {data && data.streams.length === 0 && (
          <div className="mt-10 rounded-3xl bg-navy p-8 text-center">
            <p className="speed-text text-3xl">{category ? `No ${LABEL.get(category)} streams right now` : 'Nobody’s live in public right now'}</p>
            <p className="mx-auto mt-2 max-w-md text-muted-dark">Go live and switch on Show on Live Now, and you’re the first thing people see here.</p>
            <button type="button" onClick={() => navigate('/go-live')} className="btn-race shadow-race-deep mt-6 h-14 px-8 text-xl">GO LIVE</button>
          </div>
        )}
        {data && data.streams.length > 0 && (
          <ul className="mt-6 grid gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
            {data.streams.map((s) => (
              <li key={s.id}>
                <StreamCard stream={s} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

function StreamCard({ stream }: { stream: PublicStream }) {
  const name = stream.channel.displayName ?? (stream.channel.handle ? `@${stream.channel.handle}` : 'A creator');
  // The stream's latest preview picture; the category card when there is none yet.
  const thumbnail = stream.thumbnailUrl;
  return (
    <Link to={`/s/${stream.id}`} className="group block">
      <div className="relative aspect-video overflow-hidden rounded-2xl bg-navy">
        {thumbnail ? (
          <img src={thumbnail} alt="" loading="lazy" className="h-full w-full object-cover transition group-hover:scale-[1.03]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,#24386a,#0f2148_70%)]" aria-hidden>
            <span className="text-6xl opacity-80 transition group-hover:scale-110">{CATEGORY_EMOJI[stream.category ?? 'other']}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <LiveBadge small />
          <WatchingPill viewers={stream.viewers} className="!px-2 !py-0.5 !text-xs" />
        </div>
      </div>
      <div className="mt-3 flex gap-3">
        <Avatar name={name} photoUrl={stream.channel.photoUrl} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-extrabold group-hover:text-race-300">{stream.title ?? `${name}’s stream`}</p>
          <p className="truncate text-sm text-muted-dark">{name}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-dark">
            <CategoryTag category={stream.category} />
            {stream.likes > 0 && (
              <span className="inline-flex items-center gap-1 font-bold">
                <ThumbUpIcon size={13} /> {count(stream.likes)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
